import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { User, Phone, Briefcase, Truck, Save, Loader2, CheckCircle2, Shield } from 'lucide-react';
import { keycloakAdminService } from '../../../services/keycloakAdminService';

const ProfileView = ({ currentRole }) => {
    const { user, refreshUserProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        vehicleNumber: '',
        employeeId: ''
    });

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                phone: user.phone || '',
                vehicleNumber: user.vehicleNumber || '',
                employeeId: user.employeeId || ''
            });
        }
    }, [user]);

    const isSalesPerson = currentRole === 'SALES_PERSON';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccess(false);

        try {
            await keycloakAdminService.updateUser(user.id, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                vehicleNumber: formData.vehicleNumber,
                employeeId: formData.employeeId
            });
            
            // Refresh user profile in auth context
            if (refreshUserProfile) {
                await refreshUserProfile(user);
            }

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to update profile:', error);
            alert('Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex-1 p-6 bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-y-auto pb-24 md:pb-6">
            <div className="max-w-2xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        My Profile
                    </h1>
                    <p className="text-gray-500 mt-2 font-medium">Manage your personal information and preferences</p>
                </div>

                <form onSubmit={isSalesPerson ? (e) => e.preventDefault() : handleSubmit} className="bg-white/70 backdrop-blur-md rounded-3xl p-8 border border-white/40 shadow-xl space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 ml-1">First Name</label>
                            <div className="relative group">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    className={`w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium ${isSalesPerson ? 'cursor-not-allowed opacity-70' : ''}`}
                                    required
                                    disabled={isSalesPerson}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 ml-1">Last Name</label>
                            <div className="relative group">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    className={`w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium ${isSalesPerson ? 'cursor-not-allowed opacity-70' : ''}`}
                                    required
                                    disabled={isSalesPerson}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 ml-1">Phone Number</label>
                            <div className="relative group">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className={`w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium ${isSalesPerson ? 'cursor-not-allowed opacity-70' : ''}`}
                                    placeholder="+971 50 123 4567"
                                    disabled={isSalesPerson}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 ml-1">Employee ID</label>
                            <div className="relative group">
                                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                <input
                                    type="text"
                                    value={formData.employeeId}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none transition-all font-medium cursor-not-allowed opacity-70"
                                    disabled
                                />
                                <p className="text-[10px] text-gray-400 mt-1 ml-1 font-medium italic">* Read-only. Contact admin to change.</p>
                            </div>
                        </div>

                        {isSalesPerson && (
                            <div className="space-y-2 md:col-span-2">
                                <label className="text-sm font-bold text-gray-700 ml-1">Vehicle Number</label>
                                <div className="relative group">
                                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                    <input
                                        type="text"
                                        value={formData.vehicleNumber}
                                        onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium cursor-not-allowed opacity-70"
                                        placeholder="V-12345"
                                        disabled={isSalesPerson}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {!isSalesPerson && (
                        <div className="pt-4 flex items-center justify-between gap-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 rounded-2xl font-black text-sm uppercase tracking-wider transition-all duration-300 shadow-lg ${
                                    success 
                                    ? 'bg-green-500 text-white shadow-green-200' 
                                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:scale-[1.02] active:scale-[0.98] shadow-blue-200'
                                }`}
                            >
                                {loading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : success ? (
                                    <>
                                        <CheckCircle2 size={20} />
                                        Profile Updated
                                    </>
                                ) : (
                                    <>
                                        <Save size={20} />
                                        Save Profile
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>

                <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
                    <h3 className="text-amber-800 font-bold text-sm mb-1 flex items-center gap-2">
                        <Shield size={16} />
                        Security Notice
                    </h3>
                    <p className="text-amber-700 text-xs font-medium leading-relaxed">
                        To change your password or security settings, please visit the Security section in your settings or contact your administrator if you've forgotten your credentials.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ProfileView;
